import React from 'react';
import PropTypes from 'prop-types';

import i18n from 'util/i18n';

import { HISTORY, HISTORY_EMPLOYMENT } from 'config/formSections/history';
import { formConfig } from 'config/forms';
import { findTimelineGaps, getApplicantRequiredDuration, getThresholdYears } from 'helpers/date';

import connectSubsection from 'components/Section/shared/SubsectionConnector';

import Field from 'components/Form/Field';
import Show from 'components/Form/Show';
import { SF852013 } from 'constants/formTypes';
import { isPVQForm } from 'helpers/form';
import ConnectedEmployment from './Employment';
import EmploymentSummaryProgress from './EmploymentSummaryProgress';

const sectionConfig = {
  key: HISTORY_EMPLOYMENT.key,
  section: HISTORY.name,
  subsection: HISTORY_EMPLOYMENT.name,
};

const EmploymentWrapper = function (props) {
  const {
    formType, errors, applicantBirthdate, data
  } = props;

  const formTypeConfig = formType && formConfig[formType];

  const years = formTypeConfig && formTypeConfig.HISTORY_EMPLOYMENT_YEARS;
  const recordYears = formTypeConfig && formTypeConfig.HISTORY_EMPLOYMENT_RECORD_YEARS;
  const thresholdYears = getThresholdYears(formType);

  const { coverage, requiredYears } = getApplicantRequiredDuration({
    requiredDuration: { years }, applicantBirthdate, thresholdYears, type: 'employment'
  });

  const formName = formType;

  let validEmploymentDates = data.List && data.List.items
    ? data.List.items
      .filter((i) => {
        if (!i?.Item?.Dates) { return false; }
        if (!errors || !errors.length) { return true; }
        if (errors.filter((e) => e.indexOf(i.uuid) > -1).length > 0) { return false; }
        return true;
      })
      .map((i) => i.Item.Dates)
    : [];

  if (data.List && data.List.items) {
    data.List.items.forEach((i) => {
      let validAdditionalEmploymentDates;
      if (i.Item && i.Item.Additional && i.Item.Additional.items) {
        validAdditionalEmploymentDates = i.Item.Additional.items
          .filter((d) => {
            if (!d.Item) { return false; }
            if (!errors || !errors.length) { return true; }
            if (!!i && errors.filter((e) => e.indexOf(i.uuid) > -1).length > 0) { return false; }
            return true;
          })
          .map((d) => d.Item.DatesEmployed);
      }
      if (validAdditionalEmploymentDates) { validEmploymentDates = validEmploymentDates.concat(validAdditionalEmploymentDates); }
    });
  }

  validEmploymentDates = validEmploymentDates.filter((i) => (
    !!i
    && i.from?.month !== ''
    && i.from?.day !== ''
    && i.from?.year !== ''
    && i.to?.month !== ''
    && i.to?.day !== ''
    && i.to?.year !== ''
  ));

  const gaps = findTimelineGaps(coverage, validEmploymentDates);

  return isPVQForm(formType) ? (
    <div>
      <h1 className="section-header">{i18n.t('history.pvq.employment.summary.title')}</h1>

      <Field
        optional
        className="no-margin-bottom"
      >
        <span>{i18n.t('history.pvq.employment.para.employment')} <strong>{i18n.t('history.pvq.employment.para.employment2')}</strong> {i18n.t('history.pvq.employment.para.employment3')}</span>
      </Field>

      <span id="scrollToHistory" />

      <EmploymentSummaryProgress
        years={years}
        dates={validEmploymentDates}
        applicantBirthdate={applicantBirthdate}
        thresholdYears={thresholdYears}
        formType={formType}
      />

      <ConnectedEmployment
        scrollToTop="scrollToHistory"
        totalYears={requiredYears}
        recordYears={recordYears}
        gaps={gaps}
      />

      <Show when={gaps && gaps.length > 0}>
        <div className="not-complete">
          <hr className="section-divider" />

          <Field
            title={i18n.t('history.employment.heading.exiting')}
            titleSize="h4"
            optional
            className="no-margin-bottom"
          >
            {i18n.m('history.pvq.employment.para.exiting', { years: requiredYears, formName })}
          </Field>
        </div>
      </Show>
    </div>
  ) : (
    <div>
      <h1 className="section-header">{i18n.t('history.employment.summary.title')}</h1>

      <Field
        title={i18n.t('history.employment.heading.employment')}
        titleSize="h3"
        optional
        className="no-margin-bottom"
      >
        {formType !== SF852013
          ? [
            i18n.m('history.employment.para.employment', { years: requiredYears }),
            i18n.m('history.employment.para.employment2')
          ] : i18n.m('history.employment.para.employmentSF852013')}
      </Field>

      <span id="scrollToHistory" />

      <EmploymentSummaryProgress
        years={years}
        dates={validEmploymentDates}
        applicantBirthdate={applicantBirthdate}
        thresholdYears={thresholdYears}
      />

      <ConnectedEmployment
        scrollToTop="scrollToHistory"
        totalYears={requiredYears}
        recordYears={recordYears}
        gaps={gaps}
      />

      <Show when={gaps && gaps.length > 0}>
        <div className="not-complete">
          <hr className="section-divider" />

          <Field
            title={i18n.t('history.employment.heading.exiting')}
            titleSize="h4"
            optional
            className="no-margin-bottom"
          >
            {i18n.m('history.employment.para.exiting', { years: requiredYears, formName })}
          </Field>
        </div>
      </Show>
    </div>
  );
};

/* eslint react/forbid-prop-types: 0 */
EmploymentWrapper.propTypes = {
  applicantBirthdate: PropTypes.object,
  data: PropTypes.object,
  formType: PropTypes.string.isRequired,
  errors: PropTypes.array,
};

EmploymentWrapper.defaultProps = {
  applicantBirthdate: null,
  data: {},
  errors: [],
};

export default connectSubsection(EmploymentWrapper, sectionConfig);
